/**
 * msAdmin 管理系统 - 第四步：添加 MainLayout 布局系统
 */
import React from "react";
import { BrowserRouter, useRoutes, useNavigate, useLocation } from "react-router-dom";
import { ConfigProvider, App as AntApp } from "antd";
import zhCN from "antd/locale/zh_CN";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import { useAuth } from "@ms/shared-auth";
import { createMsAdminLayoutConfig } from "./config/layoutConfig";
import { msAdminAuthConfig } from "./config/authConfig";
import { createRouteConfig } from "./config/routeCreator";
import { handleTokenFromUrl } from "./utils/tokenFromUrl";
import { API_CONFIG } from "./config/netConfig";
// import PermissionDebugTool from './components/PermissionDebugTool';

// 配置dayjs
dayjs.locale("zh-cn");

// 设置全局环境变量，确保 useAuth 使用正确的 API 地址
if (typeof window !== "undefined") {
  // 使用 API_CONFIG 中的配置，它已经根据环境正确配置了
  // API_CONFIG.BASE_URL 已经包含了 /api 前缀（生产环境）或完整的 URL（开发环境）
  const apiBaseUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}`.replace(/\/api\/api$/, "/api");
  (window as any).REACT_APP_API_BASE_URL = apiBaseUrl;
  (window as any).REACT_APP_API_URL = apiBaseUrl;

  // 开发环境调试信息（仅在开发模式下输出）
  // 注意：在生产构建时，这段代码会被优化掉，不会影响性能
  if ((window as any).process?.env?.NODE_ENV === "development") {
    console.log("🔧 msAdmin API 配置:", {
      API_CONFIG_BASE_URL: API_CONFIG.BASE_URL,
      API_CONFIG_API_PREFIX: API_CONFIG.API_PREFIX,
      finalApiBaseUrl: apiBaseUrl,
    });
  }
}

/**
 * 路由组件
 */
const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 完全禁用调试信息输出
  // console.log('[AppRoutes] 开始渲染路由组件');
  const routes = useRoutes(
    createRouteConfig(createMsAdminLayoutConfig(navigate, location.pathname), location.pathname, navigate)
  );
  // console.log('[AppRoutes] 路由渲染成功');
  return routes;
};

/**
 * 带认证的应用组件
 */
const AppWithAuth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, token, login, checkAuth } = useAuth();

  // 完全禁用调试信息输出
  // console.log('[App] 开始渲染主应用组件');

  // 处理URL中的token
  React.useEffect(() => {
    const urlToken = handleTokenFromUrl();
    if (urlToken) {
      console.log("✅ 已从URL获取并设置token，重新检查认证状态");
      checkAuth();
    }
  }, [checkAuth]);

  // 处理认证状态
  React.useEffect(() => {
    if (!isAuthenticated) {
      // 检查是否有本地token
      const localToken = localStorage.getItem("token");
      if (localToken) {
        // 验证token有效性
        checkAuth();
      } else {
        // 检查是否在iframe环境中
        if (window.parent !== window) {
          // 在iframe环境中，请求父应用提供token
          window.parent.postMessage({ type: "REQUEST_TOKEN" }, "*");
        }
      }
    }
  }, [isAuthenticated, checkAuth]);

  // 监听来自父应用的消息
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "TOKEN_UPDATE") {
        // 收到Token更新通知
        // console.log('[msAdmin] 收到Token更新通知');
        // console.log('[msAdmin] Token更新数据:', {
        //   token: event.data.token,
        //   timestamp: event.data.timestamp,
        //   source: event.data.source
        // });

        if (event.data.token) {
          localStorage.setItem("token", event.data.token);
          // console.log('[msAdmin] Token已更新，重新进行认证检查');
          checkAuth();
        }
      } else if (event.data.type === "AUTH_FAILED") {
        // 收到认证失败通知
        // console.log('[msAdmin] 收到认证失败通知');
        localStorage.removeItem("token");
        // 可以在这里添加重定向到登录页面的逻辑
      } else if (event.data.type === "CLEAR_AUTH") {
        // 收到清除认证通知
        // console.log('[msAdmin] 收到清除认证通知');
        localStorage.removeItem("token");
        // 可以在这里添加重定向到登录页面的逻辑
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [checkAuth]);

  // 在iframe环境中请求Token更新
  React.useEffect(() => {
    if (window.parent !== window) {
      // 在iframe环境中，请求Token更新
      // console.log('[msAdmin] 在iframe环境中，请求Token更新');
      // console.log('[msAdmin] 当前时间:', new Date().toISOString());
      // console.log('[msAdmin] 当前URL:', window.location.href);

      // 延迟发送请求，确保父应用已准备好
      const timer = setTimeout(() => {
        // console.log('[msAdmin] 延迟发送Token更新请求');
        window.parent.postMessage({ type: "REQUEST_TOKEN" }, "*");
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      // 不在iframe环境中，检查认证状态
      // console.log('[msAdmin] 认证失败，等待重新认证');
    }
  }, []);

  return (
    <ConfigProvider locale={zhCN}>
      <AntApp>
        <AppRoutes />
      </AntApp>
    </ConfigProvider>
  );
};

/**
 * 主应用组件
 */
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppWithAuth />
    </BrowserRouter>
  );
};

export default App;
